import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, CheckCircle2, Clock, XCircle, ScanFace, FileImage } from "lucide-react";
import { LiveFaceKYC } from "@/components/LiveFaceKYC";

function FileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function KYC() {
  const { user, refresh } = useAuth();
  const [fullName, setFullName] = useState(user?.name || "");
  const [docType, setDocType] = useState("passport");
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'live' | 'manual' | null

  const submit = async (e) => {
    e.preventDefault();
    if (!idFile || !selfieFile) { toast.error("Upload both documents"); return; }
    setBusy(true);
    try {
      const id_b64 = await FileToBase64(idFile);
      const selfie_b64 = await FileToBase64(selfieFile);
      await api.post("/kyc/submit", { id_document_b64: id_b64, selfie_b64: selfie_b64, full_name: fullName, document_type: docType });
      toast.success("KYC submitted — pending company review");
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const handleLiveComplete = async (data) => {
    try {
      const res = await api.post("/kyc/auto-verify", data);
      if (res.data?.auto) toast.success("🎉 Auto-approved via live face verification!");
      else toast.info("Submitted for manual review (liveness thresholds not met)");
      await refresh();
      setMode(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const status = user?.kyc_status || "pending";
  const StatusPill = () => {
    const map = {
      pending: { c: "amber", i: Clock, t: "Not submitted" },
      submitted: { c: "cyan", i: Clock, t: "Under review" },
      approved: { c: "green", i: CheckCircle2, t: "Approved" },
      rejected: { c: "red", i: XCircle, t: "Rejected" },
    };
    const s = map[status] || map.pending;
    const colors = { amber: "text-amber-300 bg-amber-500/10 border-amber-500/30", cyan: "text-nb-cyan bg-nb-cyan/10 border-nb-cyan/30", green: "text-[#8AE234] bg-[#8AE234]/10 border-[#8AE234]/30", red: "text-red-400 bg-red-500/10 border-red-500/30" };
    const Icon = s.i;
    return <div data-testid="kyc-status" className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border ${colors[s.c]}`}><Icon size={12}/> {s.t}</div>;
  };

  return (
    <AppLayout variant="user">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Compliance</div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">KYC Verification</h1>
        </div>
        <StatusPill />
      </div>

      {status === "approved" ? (
        <div className="nb-card p-8 text-center">
          <CheckCircle2 size={48} className="text-[#8AE234] mx-auto"/>
          <div className="font-heading text-white text-xl mt-3">You're verified</div>
          <div className="text-sm text-slate-400 mt-1">You can activate bots and request withdrawals.</div>
        </div>
      ) : !mode ? (
        <div className="grid md:grid-cols-2 gap-4">
          <button data-testid="kyc-mode-live" onClick={()=>setMode("live")} className="nb-card p-6 text-left hover:border-[#8AE234]/50 transition group">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#8AE234]/10 border border-[#8AE234]/30 flex items-center justify-center text-[#8AE234]"><ScanFace size={24}/></div>
              <div>
                <div className="font-heading text-white text-lg">Live Face Verification</div>
                <div className="text-xs text-[#8AE234] mt-0.5">RECOMMENDED · Instant approval</div>
              </div>
            </div>
            <div className="text-sm text-slate-400 mt-3">Use your camera. Blink, turn your head, and smile — we'll verify liveness on-device and approve you instantly.</div>
          </button>
          <button data-testid="kyc-mode-manual" onClick={()=>setMode("manual")} className="nb-card p-6 text-left hover:border-nb-cyan/50 transition">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-nb-cyan/10 border border-nb-cyan/30 flex items-center justify-center text-nb-cyan"><FileImage size={24}/></div>
              <div>
                <div className="font-heading text-white text-lg">Manual Upload</div>
                <div className="text-xs text-slate-400 mt-0.5">Reviewed by the company (24–48h)</div>
              </div>
            </div>
            <div className="text-sm text-slate-400 mt-3">Upload photos of your ID document and a selfie. Our compliance team will review your submission.</div>
          </button>
        </div>
      ) : mode === "live" ? (
        <LiveFaceKYC onComplete={handleLiveComplete} onCancel={()=>setMode(null)}/>
      ) : (
        <form onSubmit={submit} className="nb-card p-6 grid md:grid-cols-2 gap-6">
          <div className="md:col-span-2 flex items-center justify-between">
            <button type="button" onClick={()=>setMode(null)} className="text-xs text-slate-400 hover:text-white">← Back to verification options</button>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-400 uppercase tracking-widest">Full legal name</label>
            <input data-testid="kyc-name" required value={fullName} onChange={(e)=>setFullName(e.target.value)} className="nb-input mt-1"/>
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">Document type</label>
            <select data-testid="kyc-doctype" value={docType} onChange={(e)=>setDocType(e.target.value)} className="nb-input mt-1">
              <option value="passport">Passport</option>
              <option value="driver_license">Driver's License</option>
              <option value="national_id">National ID</option>
            </select>
          </div>
          <div/>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">ID Document Photo</label>
            <label className="mt-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/10 rounded-xl py-8 text-slate-400 hover:border-[#8AE234]/40 cursor-pointer">
              <Upload size={20}/>
              <span className="text-xs">{idFile ? idFile.name : "Click to upload ID"}</span>
              <input data-testid="kyc-id-file" type="file" accept="image/*" className="hidden" onChange={(e)=>setIdFile(e.target.files?.[0])}/>
            </label>
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">Selfie</label>
            <label className="mt-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/10 rounded-xl py-8 text-slate-400 hover:border-[#8AE234]/40 cursor-pointer">
              <Upload size={20}/>
              <span className="text-xs">{selfieFile ? selfieFile.name : "Click to upload selfie"}</span>
              <input data-testid="kyc-selfie-file" type="file" accept="image/*" className="hidden" onChange={(e)=>setSelfieFile(e.target.files?.[0])}/>
            </label>
          </div>
          <div className="md:col-span-2">
            <button data-testid="kyc-submit" disabled={busy || status === "submitted"} className="nb-btn-primary">{busy?"Submitting…":status==="submitted"?"Already submitted":"Submit for review"}</button>
          </div>
        </form>
      )}
    </AppLayout>
  );
}
